import { useMemo, type ComponentType } from 'react';
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ListIcon,
} from 'lucide-react';
import {
  Calendar,
  Views,
  type CalendarProps,
  type EventProps,
  type ToolbarProps,
} from 'react-big-calendar';
import withDragAndDropImport, {
  type EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';

import { ViewToggle } from '@/components/ui/view-toggle';
import { useLanguage } from '@/contexts/language-context';
import { cn } from '@/lib/utils';
import { dateFromISO, toISODate } from '@/routes/jobs/helpers';

import 'react-big-calendar/lib/css/react-big-calendar.css';
import 'react-big-calendar/lib/addons/dragAndDrop/styles.css';
import './calendar.css';

import { getCalendarMatrix, gridRange, startOfMonth } from './helpers';
import { calendarCulture, calendarMessages, localizer } from './localizer';
import type { AppCalendarProps, CalendarEvent, CalendarView } from './types';

/** RBC's internal event shape. The whole `CalendarEvent` rides along as `resource`. */
interface RbcEvent {
  title: string;
  start: Date;
  end: Date;
  allDay: true;
  resource: CalendarEvent;
}

// Vite's CJS→ESM interop for this addon (a default-only `__esModule` module)
// hands back a `{ default: fn }` wrapper in the browser, but the bare function
// under Vitest / the Rollup build — normalise to the function either way.
const dndImport = withDragAndDropImport as unknown as
  | typeof withDragAndDropImport
  | { default: typeof withDragAndDropImport };
const withDragAndDrop =
  typeof dndImport === 'function' ? dndImport : dndImport.default;

// `withDragAndDrop` keeps the event generic; the one cast the migration needs is
// here — a `Calendar` class narrowed to the plain props signature the HOC wants.
const DnDCalendar = withDragAndDrop<RbcEvent>(
  Calendar as unknown as ComponentType<CalendarProps<RbcEvent>>,
);

function AppEvent({ event }: EventProps<RbcEvent>) {
  return (
    <span
      className="block truncate"
      data-testid={`calendar-event-${event.resource.id}`}
      title={event.title}
    >
      {event.title}
    </span>
  );
}

function AppToolbar({ label, view, onNavigate, onView }: ToolbarProps<RbcEvent>) {
  const { t } = useLanguage();
  return (
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => onNavigate('PREV')}
          aria-label={t.calendarPrevMonth}
          data-testid="calendar-prev"
          className="rounded-md border bg-card p-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onNavigate('NEXT')}
          aria-label={t.calendarNextMonth}
          data-testid="calendar-next"
          className="rounded-md border bg-card p-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          <ChevronRightIcon className="h-4 w-4" />
        </button>
        <button
          type="button"
          onClick={() => onNavigate('TODAY')}
          data-testid="calendar-today"
          className="ml-1 rounded-md border bg-card px-2 py-1 text-xs font-medium text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
        >
          {t.today}
        </button>
      </div>

      <h2
        className="text-sm font-semibold capitalize"
        data-testid="calendar-month-label"
      >
        {label}
      </h2>

      <ViewToggle
        value={view === Views.AGENDA ? 'agenda' : 'month'}
        onChange={(next) =>
          onView(next === 'agenda' ? Views.AGENDA : Views.MONTH)
        }
        aria-label={t.calendarView}
        options={[
          {
            value: 'month',
            label: t.calendarViewMonth,
            icon: CalendarIcon,
            testId: 'calendar-view-month',
          },
          {
            value: 'agenda',
            label: t.calendarViewAgenda,
            icon: ListIcon,
            testId: 'calendar-view-agenda',
          },
        ]}
      />
    </div>
  );
}

const COMPONENTS = { toolbar: AppToolbar, event: AppEvent };

export function AppCalendar({
  events,
  date,
  view,
  onNavigate,
  onView,
  onSelectEvent,
  onEventDrop,
  draggableAccessor,
  isLoading = false,
  emptyLabel,
  agendaLength = 30,
  'data-testid': testId,
}: AppCalendarProps) {
  const { t, language } = useLanguage();

  const rbcEvents = useMemo<RbcEvent[]>(
    () =>
      events.map((event) => {
        const start = dateFromISO(event.date) ?? new Date(event.date);
        return {
          title: event.label,
          start,
          end: start,
          allDay: true,
          resource: event,
        };
      }),
    [events],
  );

  const messages = useMemo(() => calendarMessages(t), [t]);

  // Month view has no built-in empty state. Show the caption when nothing lands
  // in the visible grid (agenda has RBC's own `noEventsInRange`).
  const isEmptyMonth = useMemo(() => {
    if (view !== 'month') return false;
    const { start, end } = gridRange(getCalendarMatrix(startOfMonth(date)));
    return !events.some((e) => e.date >= start && e.date <= end);
  }, [view, date, events]);

  return (
    <div
      className={cn(
        'flex flex-col',
        isLoading && 'pointer-events-none opacity-60',
      )}
      data-testid={testId}
    >
      <div className="rbc-theme" aria-busy={isLoading}>
        <DnDCalendar
          localizer={localizer}
          culture={calendarCulture(language)}
          events={rbcEvents}
          date={date}
          view={view}
          onNavigate={(next) => onNavigate(next)}
          onView={(next) => onView(next as CalendarView)}
          views={[Views.MONTH, Views.AGENDA]}
          length={agendaLength}
          popup
          resizable={false}
          messages={messages}
          components={COMPONENTS}
          startAccessor="start"
          endAccessor="end"
          allDayAccessor="allDay"
          titleAccessor="title"
          eventPropGetter={(event) => ({
            className: `tone-${event.resource.tone ?? 'default'}`,
          })}
          onSelectEvent={(event) => onSelectEvent(event.resource)}
          draggableAccessor={(event) =>
            draggableAccessor ? draggableAccessor(event.resource) : true
          }
          onEventDrop={
            onEventDrop
              ? ({ event, start }: EventInteractionArgs<RbcEvent>) =>
                  onEventDrop({
                    event: event.resource,
                    date: toISODate(
                      start instanceof Date ? start : new Date(start),
                    ),
                  })
              : undefined
          }
        />
      </div>

      {emptyLabel && isEmptyMonth && (
        <p
          className="pt-3 text-center text-xs text-muted-foreground"
          data-testid="calendar-empty"
        >
          {emptyLabel}
        </p>
      )}
    </div>
  );
}
